import { z } from "zod";
import { customerSchema, previousCustomerSchema } from "@/types/schemas/customer";
import { addressWithTypeSchema } from "@/types/schemas/address";
import { isArray } from "remeda";
import { executivePersonSchema } from "@/types/schemas/person";
import { supplyPointSchema } from "@/types/schemas/supplyPoints";
import { paymentInfoSchema } from "@/types/schemas/paymentInfo";
import { contractDetailsSchema, contractInfoSchema, econtractInfoSchema } from "@/types/schemas/contractInfo";
import { timestampSchema } from "@/types/schemas/timestamps";
import { agreementSchema } from "@/types/schemas/agreements";
import { templateSchema } from "@/types/schemas/templates";
import { attachmentSchema } from "@/types/schemas/attachment";
import { bonusSchema } from "@/types/schemas/bonuses";
import { bundleServiceSchema } from "@/types/schemas/bundleServices";
import { PhaseEnumSchema, ProcessEnumSchema, ProcessSpecificationEnumSchema, SegmentEnumSchema } from "@/types/contract";
import { meterReadingsSchema } from "@/types/schemas/meterReadings";
import { campaignsSchema } from "./campaigns";
import { recontractingRequestSchema } from "./recontractingRequest";
import { paymentsSchema } from "./payments";
import { dateSchema } from "./date";
import { vehicleSchema } from "./vehicles";
import { previousProductSchema } from "./previousProduct";

export const contractSchema = z
  .object({
    id: z.string().or(z.number()),
    parentContractId: z.string().or(z.number()).nullable().default(null),
    state: z.string().nullable().default(null),
    contractNumber: z.preprocess(contractNumber => (contractNumber === null ? "" : contractNumber), z.string()),
    urgent: z.boolean(),
    segment: SegmentEnumSchema,
    sellChannel: z.string().nullable().default(null),
    sellChannelType: z.string().nullable().default(null),
    authorId: z.string().or(z.number()).nullable().default(null),
    process: ProcessEnumSchema,
    segmentServiceClass: z.string().nullable().default(null),
    channel: z.string(),
    processSpecification: ProcessSpecificationEnumSchema,
    enumerationLastModified: z.number().nullable().default(null),
    stateChangedAt: z.number().nullable().default(null),
    econtractInfo: econtractInfoSchema,
    contractNote: z.string().nullable().default(null),
    gdprAnonymization: z.boolean(),
    contractInfo: contractInfoSchema,
    differentCustomer: z.unknown().nullable().default(null),
    executivePeople: z.array(executivePersonSchema),
    supplyPoints: z.array(supplyPointSchema),
    meterReadings: z.array(meterReadingsSchema),
    bonuses: z.array(bonusSchema).default([]),
    contractStatus: z.string().nullable().default(null),
    greenChangeAllowed: z.boolean().nullable().default(null),
    contractSource: z.string().nullable().default(null),
    paymentInfo: paymentInfoSchema.nullable().default(null),
    campaign: campaignsSchema.nullable().default(null),
    additionalDocuments: z.array(z.string()).nullable().default([]),
    previousCustomerAdditionalDocuments: z.array(z.string()).nullable().default([]),
    contractDetails: contractDetailsSchema.nullable().default(null),
    bundleServices: z.array(bundleServiceSchema),
    timestamps: z.array(timestampSchema),
    agreements: z.array(agreementSchema),
    attachments: z.union([
      z.array(attachmentSchema),
      z.object({
        powerOfAttorney: z.string().nullable().default(null),
        contract: z.string().nullable().default(null),
      }),
    ]),
    verificationPayment: z.unknown().nullable().default(null),
    customerIdentityCheck: z.unknown().nullable().default(null),
    templates: z.array(templateSchema).default([]),
    greenProduct: z.boolean().nullable().default(null),
    ecoType: z.string().nullable().default(null),
    recontractingRequest: recontractingRequestSchema.nullable().default(null),
    vehicles: z.array(vehicleSchema).default([]),
    previousProduct: previousProductSchema.nullable().default(null),
    customer: customerSchema,
    oldCustomer: customerSchema.nullable().default(null),
    previousCustomer: previousCustomerSchema.nullable().default(null),
    addresses: z.preprocess(addr => (isArray(addr) && addr.length === 0 ? [] : addr), z.array(addressWithTypeSchema)),
    selfService: z.boolean().nullable().default(false),
    processSpecificationId: z.number().nullable().default(null),
    verification: z.string().nullable().default(null),
    verificationReason: z.string().nullable().default(null),
    uuid: z.string().nullable().default(null),
    relatedRequest: z.string().nullable().default(null),
    processingType: z.string().nullable().default(null),
    phase: PhaseEnumSchema.nullable().default(null),
    crmObjectId: z.string().nullable().default(null),
    contractType: z.enum(["requests", "contracts"]).nullable().default(null),
    isuContractId: z.string().or(z.number()).nullable().default(null),
    payments: z.array(paymentsSchema).default([]),
    processReason: z.string().nullable().default(null),
    relatedContract: z.string().nullable().default(null),
    requestMethod: z.string().nullable().default(null),
    contractMethod: z.string().nullable().default(null),
    requestStatus: z.string().nullable().default(null),
    newCustomer: customerSchema.nullable().default(null),
    identificationFeActivation: z.boolean().default(false),
    canBeCancelled: z.boolean().nullable().default(null),
    cancelReason: z.string().nullable().default(null),
    baseDate: dateSchema,
    baseDateFrom: dateSchema,
    baseDateTo: dateSchema,
    customerEdit: z.boolean().default(false),
    customerReturn: z.boolean().default(false),
    customerReturnNote: z.string().nullable().default(null),
    saveDoubleMeasurement: z.boolean().nullable().default(null),
    requiredForContract: z.string().nullable().default(null),
    rsNote: z.string().nullable().default(null),
    ocrFeActivation: z.boolean().nullable().default(false),
  })
  .transform(contract => {
    const { supplyPoints, attachments, ...rest } = contract;

    const isAttachmentsArray = isArray(attachments);

    return {
      ...rest,
      contractMethod: contract.contractDetails?.contractMethod ?? null,
      supplyPoint: contract.supplyPoints[0]!,
      attachments: isAttachmentsArray ? attachments : [],
      attachmentDocuments: isAttachmentsArray ? null : attachments,
    };
  });

export const generatedContractSchema = z
  .object({
    id: z.number(),
    sessionKey: z.string(),
    attachments: z.object({
      powerOfAttorneyTemplate: z.string().nullable().default(null),
      powerOfAttorney: z.string().nullable().default(null),
      contract: z.string().nullable().default(null),
      contractDraft: z.string(),
      contractGenerationFailed: z.boolean().nullable(),
      attachment: z.string().nullable().default(null),
    }),
    phase: PhaseEnumSchema.optional().nullable().default(null),
    templates: z.nullable(z.array(templateSchema)).default([]),
    executivePeople: z.array(executivePersonSchema),
    supplyPoints: z.array(supplyPointSchema),
    customerId: z.string().nullable(),
    crmObjectId: z.string().nullable(),
    processSpecificationId: z.number().nullable().default(null),
  })
  .transform(contract => {
    const { supplyPoints, ...rest } = contract;
    return {
      ...rest,
      supplyPoint: contract.supplyPoints[0]!,
    };
  });

export type GeneratedContract = z.infer<typeof generatedContractSchema>;
export type PreviousCustomer = z.infer<typeof previousCustomerSchema>;
